import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from '../../../config/AuthContext';
import {
    Button,
    Space,
    Typography,
    Tooltip,
    Select,
    Input,
    Tag,
    Modal,
    message,
    Card,
    Alert,
    Divider,
    Radio,
    Checkbox,
    Collapse
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ArrowLeftOutlined,
    SaveOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    ReloadOutlined,
    AppstoreOutlined,
    ApartmentOutlined,
    BranchesOutlined,
    NodeIndexOutlined,
    LinkOutlined
} from '@ant-design/icons';
import {
    ErDiagramData,
    ErEntity,
    ErRelationship,
    ErAttribute,
    ErConnection,
    ErHierarchy,
    ErCardinality,
    ErParticipation,
    createDefaultErDiagram
} from './model/ErModel';
import { validateErDiagram, ValidationResult } from './engine/ErValidator';

const { Text, Title } = Typography;
const { Option } = Select;

interface ErDiagramIdeProps {
    initialData?: ErDiagramData | string | null;
    solutionData?: ErDiagramData | string | null;
    readOnly?: boolean;
    isTeacherMode?: boolean; // Si és professor configurant la solució
    hideImportExport?: boolean;
    hideSave?: boolean;
    hideEvaluate?: boolean;
    onSave?: (data: ErDiagramData) => void;
    onManualSave?: (data: ErDiagramData) => void | Promise<void>;
    onSubmitEvaluation?: (data: ErDiagramData, result: ValidationResult) => void;
    customConfig?: any;
    intentsFallitsPrevis?: number;
    title?: string;
    onBack?: () => void;
}

export const ErDiagramIde: React.FC<ErDiagramIdeProps> = ({
    initialData,
    solutionData,
    readOnly = false,
    isTeacherMode = false,
    hideImportExport = false,
    hideSave = false,
    hideEvaluate = false,
    onSave,
    onManualSave,
    onSubmitEvaluation,
    customConfig,
    intentsFallitsPrevis = 0,
    title = 'IDE de Disseny: Model Entitat-Relació (ER)',
    onBack
}) => {
    // Estat principal del diagrama
    const [diagram, setDiagram] = useState<ErDiagramData>(() => {
        if (!initialData) return createDefaultErDiagram();
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && parsed.type === 'ER') return parsed;
            } catch (e) { }
            return createDefaultErDiagram();
        }
        return initialData;
    });

    // Element seleccionat actualment
    const [selectedElement, setSelectedElement] = useState<{
        type: 'entity' | 'relationship' | 'attribute' | 'connection' | 'hierarchy';
        id: string;
    } | null>(null);

    // Mode d'eina activa a la barra d'eines
    const [toolMode, setToolMode] = useState<
        'select' | 'add_entity' | 'add_weak_entity' | 'add_relationship' | 'add_weak_relationship' |
        'add_attribute' | 'add_pk' | 'add_weak_pk' | 'add_multivalued' | 'add_derived' | 'add_connection' | 'add_hierarchy'
    >('select');

    // Sessió d'usuari per determinar permisos
    const { user } = useContext(AuthContext);
    const isTeacherOrAdmin = user?.tipus === '0' || user?.tipus === '1' || isTeacherMode;

    // Categoria activa a la paleta lateral esquerra
    const [activeCategory, setActiveCategory] = useState<'entity' | 'relationship' | 'attribute' | 'hierarchy' | 'connection'>('entity');
    const [entitySubtype, setEntitySubtype] = useState<'normal' | 'weak'>('normal');
    const [relSubtype, setRelSubtype] = useState<'normal' | 'weak'>('normal');
    const [relCard, setRelCard] = useState<ErCardinality>('1:N');
    const [saving, setSaving] = useState(false);
    const [attrSubtype, setAttrSubtype] = useState<'normal' | 'pk' | 'weak_pk' | 'multi' | 'derived'>('normal');
    const [hierCoverage, setHierCoverage] = useState<'t' | 'p'>('t');
    const [hierDisjoint, setHierDisjoint] = useState<'d' | 's'>('d');

    // Estat auxiliar per a la creació de connexions (pas 1: clic origen, pas 2: clic destí)
    const [connectionSourceId, setConnectionSourceId] = useState<string | null>(null);

    // Zoom i desplaçament
    const [zoom, setZoom] = useState<number>(1);
    const [isDraggingNode, setIsDraggingNode] = useState<string | null>(null);
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const lastMousePosRef = useRef<{ x: number; y: number } | null>(null);

    // Resultat d'avaluació
    const [evaluationResult, setEvaluationResult] = useState<ValidationResult | null>(null);
    const [evalModalVisible, setEvalModalVisible] = useState(false);

    // Referència al contenidor SVG
    const svgRef = useRef<SVGSVGElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Sincronitzar amb initialData quan canvia la prop (per exemple, en obrir el modal o rebre les dades de l'exercici)
    const isFirstMountRef = useRef(true);
    useEffect(() => {
        if (!initialData) return;
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && (parsed.type === 'ER' || Array.isArray(parsed.entities))) {
                    setDiagram(parsed);
                }
            } catch (e) { }
        } else if (initialData && ((initialData as any).type === 'ER' || Array.isArray((initialData as any).entities))) {
            setDiagram(initialData);
        }
    }, [initialData]);

    // Notificar canvi / Auto-guardat debounced (només quan l'usuari ha interactuat, no en el muntatge inicial)
    useEffect(() => {
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            return;
        }
        if (onSave) {
            const timer = setTimeout(() => {
                onSave(diagram);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [diagram, onSave]);

    // Posicions i dimensions per defecte
    const ENTITY_W = 120;
    const ENTITY_H = 46;
    const REL_SIZE = 70; // Diàmetre/Ample rombe
    const ATTR_RX = 46;
    const ATTR_RY = 22;

    // Utilitats d'obtenció de coordenades centrals
    const getNodeCenter = (id: string): { x: number; y: number } => {
        const ent = diagram.entities.find(e => e.id === id);
        if (ent) return { x: ent.x + (ent.width || ENTITY_W) / 2, y: ent.y + (ent.height || ENTITY_H) / 2 };

        const rel = diagram.relationships.find(r => r.id === id);
        if (rel) return { x: rel.x + (rel.width || REL_SIZE) / 2, y: rel.y + (rel.height || REL_SIZE) / 2 };

        const attr = diagram.attributes.find(a => a.id === id);
        if (attr) return { x: attr.x, y: attr.y };

        const hier = diagram.hierarchies.find(h => h.id === id);
        if (hier) return { x: hier.x, y: hier.y };

        return { x: 0, y: 0 };
    };

    // Crear nous elements al fer clic al llenç
    const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
        if (readOnly) return;
        if (toolMode === 'select') {
            setSelectedElement(null);
            setConnectionSourceId(null);
            return;
        }

        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = (e.clientX - rect.left) / zoom;
        const y = (e.clientY - rect.top) / zoom;
        const newId = `node_${Date.now()}`;

        if (toolMode === 'add_entity' || toolMode === 'add_weak_entity') {
            const isWeak = toolMode === 'add_weak_entity' || entitySubtype === 'weak';
            const newEntity: ErEntity = {
                id: newId,
                name: isWeak ? 'ENTITAT_FEBLE' : 'ENTITAT',
                isWeak: isWeak,
                x: Math.round(x - ENTITY_W / 2),
                y: Math.round(y - ENTITY_H / 2),
                width: ENTITY_W,
                height: ENTITY_H
            };
            setDiagram(prev => ({ ...prev, entities: [...prev.entities, newEntity] }));
            setSelectedElement({ type: 'entity', id: newId });
            setToolMode('select');
        } else if (toolMode === 'add_relationship' || toolMode === 'add_weak_relationship') {
            const isWeak = toolMode === 'add_weak_relationship' || relSubtype === 'weak';
            const newRel: ErRelationship = {
                id: newId,
                name: 'RELACIO',
                isWeak: isWeak,
                cardinality: relCard || '1:N',
                x: Math.round(x - REL_SIZE / 2),
                y: Math.round(y - REL_SIZE / 2),
                width: REL_SIZE,
                height: REL_SIZE
            };
            setDiagram(prev => ({ ...prev, relationships: [...prev.relationships, newRel] }));
            setSelectedElement({ type: 'relationship', id: newId });
            setToolMode('select');
        } else if (toolMode.startsWith('add_') && toolMode.includes('pk') || toolMode === 'add_attribute' || toolMode === 'add_multivalued' || toolMode === 'add_derived') {
            // Un atribut nou
            const isPk = toolMode === 'add_pk' || (toolMode === 'add_attribute' && attrSubtype === 'pk');
            const isWeakPk = toolMode === 'add_weak_pk' || (toolMode === 'add_attribute' && attrSubtype === 'weak_pk');
            const isMulti = toolMode === 'add_multivalued' || (toolMode === 'add_attribute' && attrSubtype === 'multi');
            const isDerived = toolMode === 'add_derived' || (toolMode === 'add_attribute' && attrSubtype === 'derived');

            const newAttr: ErAttribute = {
                id: newId,
                parentId: '',
                name: isPk ? 'id' : (isWeakPk ? 'id_parcial' : 'atribut'),
                isPK: isPk,
                isWeakPK: isWeakPk,
                isMultivalued: isMulti,
                isDerived: isDerived,
                x: Math.round(x),
                y: Math.round(y),
                width: ATTR_RX * 2,
                height: ATTR_RY * 2
            };
            setDiagram(prev => ({ ...prev, attributes: [...prev.attributes, newAttr] }));
            setSelectedElement({ type: 'attribute', id: newId });
            setToolMode('select');
        } else if (toolMode === 'add_hierarchy') {
            const newHier: ErHierarchy = {
                id: newId,
                superEntityId: '',
                subEntityIds: [],
                coverage: hierCoverage || 't',
                disjointness: hierDisjoint || 'd',
                x: Math.round(x),
                y: Math.round(y)
            };
            setDiagram(prev => ({ ...prev, hierarchies: [...prev.hierarchies, newHier] }));
            setSelectedElement({ type: 'hierarchy', id: newId });
            setToolMode('select');
        }
    };

    // Afegir directament un element amb el botó '+' de la barra lateral (sense especificar res més)
    const handleQuickAdd = (type: 'entity' | 'relationship' | 'attribute' | 'hierarchy') => {
        if (readOnly) return;
        const newId = `node_${Date.now()}`;
        // Posicionem al centre aproximat del llenç amb una mica d'offset aleatori
        const offset = (Math.random() * 80) - 40;
        const x = 320 + offset;
        const y = 220 + offset;

        if (type === 'entity') {
            const newEntity: ErEntity = {
                id: newId,
                name: 'ENTITAT',
                isWeak: false,
                x: Math.round(x - ENTITY_W / 2),
                y: Math.round(y - ENTITY_H / 2),
                width: ENTITY_W,
                height: ENTITY_H
            };
            setDiagram(prev => ({ ...prev, entities: [...prev.entities, newEntity] }));
            setSelectedElement({ type: 'entity', id: newId });
            setToolMode('select');
            message.success('Nova entitat afegida. Configura-la al menú.');
        } else if (type === 'relationship') {
            const newRel: ErRelationship = {
                id: newId,
                name: 'RELACIO',
                isWeak: false,
                cardinality: '1:N',
                x: Math.round(x - REL_SIZE / 2),
                y: Math.round(y - REL_SIZE / 2),
                width: REL_SIZE,
                height: REL_SIZE
            };
            setDiagram(prev => ({ ...prev, relationships: [...prev.relationships, newRel] }));
            setSelectedElement({ type: 'relationship', id: newId });
            setToolMode('select');
            message.success('Nova relació afegida. Configura-la al menú.');
        } else if (type === 'attribute') {
            const newAttr: ErAttribute = {
                id: newId,
                parentId: '',
                name: 'atribut',
                isPK: false,
                isWeakPK: false,
                isMultivalued: false,
                isDerived: false,
                x: Math.round(x),
                y: Math.round(y),
                width: ATTR_RX * 2,
                height: ATTR_RY * 2
            };
            setDiagram(prev => ({ ...prev, attributes: [...prev.attributes, newAttr] }));
            setSelectedElement({ type: 'attribute', id: newId });
            setToolMode('select');
            message.success('Nou atribut afegit. Configura-la al menú.');
        } else if (type === 'hierarchy') {
            const newHier: ErHierarchy = {
                id: newId,
                superEntityId: '',
                subEntityIds: [],
                coverage: 't',
                disjointness: 'd',
                x: Math.round(x),
                y: Math.round(y)
            };
            setDiagram(prev => ({ ...prev, hierarchies: [...prev.hierarchies, newHier] }));
            setSelectedElement({ type: 'hierarchy', id: newId });
            setToolMode('select');
            message.success('Nova jerarquia afegida. Configura-la al menú.');
        }
    };

    // Gestionar connexió entre 2 elements
    const handleNodeClickForConnection = (nodeId: string, nodeType: 'entity' | 'relationship' | 'attribute' | 'hierarchy', e: React.MouseEvent) => {
        e.stopPropagation();

        if (toolMode === 'add_connection') {
            if (!connectionSourceId) {
                // Pas 1: Seleccionem l'element origen
                setConnectionSourceId(nodeId);
                message.info('Element origen seleccionat. Clica sobre el segon element per connectar-los.');
            } else {
                // Pas 2: Creem l'enllaç entre connectionSourceId i nodeId
                if (connectionSourceId === nodeId) {
                    message.warning('No es pot connectar un element amb si mateix.');
                    setConnectionSourceId(null);
                    return;
                }

                // Si un és atribut i l'altre entitat/relació, enllacem el parentId de l'atribut
                const attr = diagram.attributes.find(a => a.id === connectionSourceId || a.id === nodeId);
                const parentCandidate = connectionSourceId === attr?.id ? nodeId : connectionSourceId;

                if (attr) {
                    setDiagram(prev => ({
                        ...prev,
                        attributes: prev.attributes.map(a => a.id === attr.id ? { ...a, parentId: parentCandidate } : a),
                        connections: [
                            ...prev.connections,
                            { id: `conn_${Date.now()}`, from: connectionSourceId, to: nodeId }
                        ]
                    }));
                } else {
                    // Connexió entre entitat i relació
                    const isBetweenEntityAndRel =
                        (diagram.entities.some(e => e.id === connectionSourceId) && diagram.relationships.some(r => r.id === nodeId)) ||
                        (diagram.relationships.some(r => r.id === connectionSourceId) && diagram.entities.some(e => e.id === nodeId));

                    const newConn: ErConnection = {
                        id: `conn_${Date.now()}`,
                        from: connectionSourceId,
                        to: nodeId,
                        participation: isBetweenEntityAndRel ? '(1,1)' : undefined
                    };
                    setDiagram(prev => ({ ...prev, connections: [...prev.connections, newConn] }));
                }

                message.success('Connexió creada correctament');
                setConnectionSourceId(null);
                setToolMode('select');
            }
            return;
        }

        setSelectedElement({ type: nodeType, id: nodeId });
    };

    // Arrossegar nodes (Drag & Drop)
    const handleMouseDown = (nodeId: string, e: React.MouseEvent) => {
        if (readOnly || toolMode !== 'select') return;
        e.stopPropagation();
        setIsDraggingNode(nodeId);

        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) {
            lastMousePosRef.current = {
                x: (e.clientX - rect.left) / zoom,
                y: (e.clientY - rect.top) / zoom
            };
        }

        const center = getNodeCenter(nodeId);
        setDragOffset({
            x: e.clientX / zoom - center.x,
            y: e.clientY / zoom - center.y
        });
    };

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        if (!isDraggingNode || readOnly) return;
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;

        const currentMouseX = (e.clientX - rect.left) / zoom;
        const currentMouseY = (e.clientY - rect.top) / zoom;

        // Calcular el desplaçament relatiu d'aquest pas
        const deltaX = lastMousePosRef.current ? Math.round(currentMouseX - lastMousePosRef.current.x) : 0;
        const deltaY = lastMousePosRef.current ? Math.round(currentMouseY - lastMousePosRef.current.y) : 0;
        lastMousePosRef.current = { x: currentMouseX, y: currentMouseY };

        setDiagram(prev => {
            // Obtenir quins atributs estan vinculats a l'element que s'arrossega (per parentId o per connexió directa)
            const isEntityOrRel = prev.entities.some(e => e.id === isDraggingNode) || prev.relationships.some(r => r.id === isDraggingNode);
            const connectedAttrIds = new Set<string>();

            if (isEntityOrRel) {
                // Atributs amb parentId directe
                prev.attributes.forEach(att => {
                    if (att.parentId === isDraggingNode) {
                        connectedAttrIds.add(att.id);
                    }
                });
                // Atributs connectats per connexions
                prev.connections.forEach(conn => {
                    if (conn.from === isDraggingNode && prev.attributes.some(a => a.id === conn.to)) {
                        connectedAttrIds.add(conn.to);
                    } else if (conn.to === isDraggingNode && prev.attributes.some(a => a.id === conn.from)) {
                        connectedAttrIds.add(conn.from);
                    }
                });
            }

            const nextEntities = prev.entities.map(ent => {
                if (ent.id === isDraggingNode) {
                    return { ...ent, x: Math.round(currentMouseX - (ent.width || ENTITY_W) / 2), y: Math.round(currentMouseY - (ent.height || ENTITY_H) / 2) };
                }
                return ent;
            });

            const nextRels = prev.relationships.map(rel => {
                if (rel.id === isDraggingNode) {
                    return { ...rel, x: Math.round(currentMouseX - (rel.width || REL_SIZE) / 2), y: Math.round(currentMouseY - (rel.height || REL_SIZE) / 2) };
                }
                return rel;
            });

            const nextAttrs = prev.attributes.map(att => {
                if (att.id === isDraggingNode) {
                    return { ...att, x: Math.round(currentMouseX), y: Math.round(currentMouseY) };
                }
                // Si s'arrossega la seva entitat o relació pare, moure també l'atribut en bloc mantenint la distància
                if (connectedAttrIds.has(att.id)) {
                    return { ...att, x: att.x + deltaX, y: att.y + deltaY };
                }
                return att;
            });

            const nextHiers = prev.hierarchies.map(h => {
                if (h.id === isDraggingNode) {
                    return { ...h, x: Math.round(currentMouseX), y: Math.round(currentMouseY) };
                }
                return h;
            });

            return {
                ...prev,
                entities: nextEntities,
                relationships: nextRels,
                attributes: nextAttrs,
                hierarchies: nextHiers
            };
        });
    };

    const handleMouseUp = () => {
        setIsDraggingNode(null);
        lastMousePosRef.current = null;
    };

    // Eliminar element seleccionat
    const handleDeleteSelected = () => {
        if (!selectedElement || readOnly) return;
        const { type, id } = selectedElement;

        setDiagram(prev => {
            return {
                ...prev,
                entities: prev.entities.filter(e => e.id !== id),
                relationships: prev.relationships.filter(r => r.id !== id),
                attributes: prev.attributes.filter(a => a.id !== id),
                hierarchies: prev.hierarchies.filter(h => h.id !== id),
                connections: prev.connections.filter(c => c.id !== id && c.from !== id && c.to !== id)
            };
        });

        setSelectedElement(null);
        message.info('Element eliminat');
    };

    // Exportar JSON a fitxer descarregable
    const handleExportJson = () => {
        const jsonStr = JSON.stringify(diagram, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `model_er_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('Model ER exportat correctament en format JSON');
    };

    // Importar JSON des de fitxer
    const handleImportJsonFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const parsed = JSON.parse(evt.target?.result as string);
                if (parsed && (parsed.type === 'ER' || Array.isArray(parsed.entities))) {
                    setDiagram(parsed);
                    message.success('Diagrama ER carregat correctament!');
                } else {
                    message.error('El fitxer no conté una estructura vàlida de Model ER');
                }
            } catch (err) {
                message.error('Error al llegir el fitxer JSON');
            }
        };
        reader.readAsText(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Avaluació automàtica contra la solució
    const handleEvaluate = () => {
        let sol: ErDiagramData | null = null;
        if (typeof solutionData === 'string') {
            try {
                sol = JSON.parse(solutionData);
            } catch (e) { }
        } else if (solutionData) {
            sol = solutionData;
        }

        if (!sol) {
            message.warning("No hi ha cap solució definida pel professor per a aquest exercici.");
            return;
        }

        const res = validateErDiagram(diagram, sol, customConfig, intentsFallitsPrevis);
        setEvaluationResult(res);
        setEvalModalVisible(true);

        if (onSubmitEvaluation) {
            onSubmitEvaluation(diagram, res);
        }
    };

    // Element actiu per editar propietats al panell lateral/superior
    const currentEntity = selectedElement?.type === 'entity' ? diagram.entities.find(e => e.id === selectedElement.id) : null;
    const currentRel = selectedElement?.type === 'relationship' ? diagram.relationships.find(r => r.id === selectedElement.id) : null;
    const currentAttr = selectedElement?.type === 'attribute' ? diagram.attributes.find(a => a.id === selectedElement.id) : null;
    const currentConn = selectedElement?.type === 'connection' ? diagram.connections.find(c => c.id === selectedElement.id) : null;
    const currentHier = selectedElement?.type === 'hierarchy' ? diagram.hierarchies.find(h => h.id === selectedElement.id) : null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', background: '#f5f7fa', overflow: 'hidden' }}>
            {/* Capçalera de l'IDE */}
            <div style={{
                background: '#1f2430',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#fff',
                borderBottom: '1px solid #333'
            }}>
                <Space size="middle">
                    {onBack && (
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined style={{ color: '#fff' }} />}
                            onClick={onBack}
                            style={{ color: '#fff' }}
                        >
                            Tornar
                        </Button>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#40a9ff' }}>
                        {title}
                    </span>
                    {isTeacherMode && <Tag color="gold">Mode Solució Professor</Tag>}
                </Space>

                <Space size="small">
                    <Tooltip title="Apropar">
                        <Button size="small" icon={<ZoomInOutlined />} onClick={() => setZoom(z => Math.min(2, z + 0.1))} />
                    </Tooltip>
                    <Tooltip title="Allunyar">
                        <Button size="small" icon={<ZoomOutOutlined />} onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} />
                    </Tooltip>
                    <Tooltip title="Reiniciar Zoom (100%)">
                        <Button size="small" onClick={() => setZoom(1)}>100%</Button>
                    </Tooltip>

                    {isTeacherOrAdmin && !hideImportExport && (
                        <>
                            <Divider type="vertical" style={{ borderColor: '#555' }} />

                            <Tooltip title="Importar diagrama JSON">
                                <Button
                                    size="small"
                                    icon={<UploadOutlined />}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Importar
                                </Button>
                            </Tooltip>
                            <input
                                type="file"
                                accept=".json"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleImportJsonFile}
                            />

                            <Tooltip title="Exportar diagrama a JSON">
                                <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportJson}
                                >
                                    Exportar
                                </Button>
                            </Tooltip>
                        </>
                    )}

                    {!readOnly && !hideSave && (
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="small"
                            loading={saving}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    if (onManualSave) {
                                        await onManualSave(diagram);
                                    } else {
                                        onSave?.(diagram);
                                        message.success('Diagrama guardat!');
                                    }
                                } catch (e: any) {
                                    message.error('Error en desar: ' + (e?.message || e));
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            Desar
                        </Button>
                    )}

                    {!hideEvaluate && !isTeacherMode && solutionData && (
                        <Button
                            type="primary"
                            style={{ background: '#52c41a', borderColor: '#52c41a', fontWeight: 600 }}
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={handleEvaluate}
                        >
                            Avaluar & Validar
                        </Button>
                    )}
                </Space>
            </div>



            {/* Cos Principal: Paleta Lateral Esquerra + Llenç Gràfic SVG */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
                {/* Menú Selector d'Objectes a l'Esquerra */}
                {!readOnly && (
                    <div style={{
                        width: 250,
                        background: '#ffffff',
                        borderRight: '1px solid #e8e8e8',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '2px 0 8px rgba(0,0,0,0.03)',
                        zIndex: 10,
                        overflowY: 'auto'
                    }}>
                        <div style={{
                            padding: '10px 12px',
                            background: '#fafafa',
                            borderBottom: '1px solid #f0f0f0'
                        }}>
                            <div style={{
                                fontWeight: 700,
                                fontSize: 11,
                                color: '#595959',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: 8
                            }}>
                                Afegir Elements
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <Button
                                    block
                                    size="small"
                                    type="default"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleQuickAdd('entity')}
                                    style={{ textAlign: 'left', fontWeight: 600, color: '#1f2937' }}
                                >
                                    Entitat
                                </Button>
                                <Button
                                    block
                                    size="small"
                                    type="default"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleQuickAdd('relationship')}
                                    style={{ textAlign: 'left', fontWeight: 600, color: '#1f2937' }}
                                >
                                    Relació
                                </Button>
                                <Button
                                    block
                                    size="small"
                                    type="default"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleQuickAdd('attribute')}
                                    style={{ textAlign: 'left', fontWeight: 600, color: '#1f2937' }}
                                >
                                    Atribut
                                </Button>
                                <Button
                                    block
                                    size="small"
                                    type="default"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleQuickAdd('hierarchy')}
                                    style={{ textAlign: 'left', fontWeight: 600, color: '#1f2937' }}
                                >
                                    Jerarquia
                                </Button>
                                <Button
                                    block
                                    size="small"
                                    type={toolMode === 'add_connection' ? 'primary' : 'default'}
                                    icon={<LinkOutlined />}
                                    onClick={() => {
                                        if (toolMode === 'add_connection') {
                                            setToolMode('select');
                                            setConnectionSourceId(null);
                                        } else {
                                            setToolMode('add_connection');
                                            setConnectionSourceId(null);
                                            message.info("Mode Enllaç: clica l'element origen i després l'element destí.");
                                        }
                                    }}
                                    style={{
                                        textAlign: 'left',
                                        fontWeight: 600,
                                        color: toolMode === 'add_connection' ? '#fff' : '#1f2937',
                                        backgroundColor: toolMode === 'add_connection' ? '#1890ff' : undefined
                                    }}
                                >
                                    {toolMode === 'add_connection' ? (connectionSourceId ? '🔗 Clica destí...' : '🔗 Clica origen...') : 'Enllaç'}
                                </Button>
                            </div>
                        </div>

                        {/* Part inferior: Edició de l'element seleccionat o acabat de crear */}
                        <div style={{ padding: '12px', flex: 1, overflowY: 'auto' }}>
                            <div style={{
                                fontWeight: 700,
                                fontSize: 11,
                                color: '#595959',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: 10,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span>{selectedElement ? `Editar ${selectedElement.type}` : "Propietats"}</span>
                                {selectedElement && (
                                    <Button
                                        size="small"
                                        type="text"
                                        onClick={() => setSelectedElement(null)}
                                        style={{ fontSize: 11, padding: 0 }}
                                    >
                                        ✕ Deseleccionar
                                    </Button>
                                )}
                            </div>

                            {selectedElement ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Tag color="blue" style={{ fontSize: 11, fontWeight: 600 }}>{selectedElement.type.toUpperCase()}</Tag>
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleDeleteSelected}
                                        >
                                            Eliminar
                                        </Button>
                                    </div>

                                    {/* 1. EDITAR ENTITAT */}
                                    {currentEntity && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Nom de l'Entitat:</Text>
                                                <Input
                                                    size="small"
                                                    value={currentEntity.name}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            entities: prev.entities.map(ent => ent.id === currentEntity.id ? { ...ent, name: val } : ent)
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Tipus d'Entitat:</Text>
                                                <Radio.Group
                                                    size="small"
                                                    value={currentEntity.isWeak}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            entities: prev.entities.map(ent => ent.id === currentEntity.id ? { ...ent, isWeak: val } : ent)
                                                        }));
                                                    }}
                                                    style={{ display: 'flex', width: '100%' }}
                                                >
                                                    <Radio.Button value={false} style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Regular</Radio.Button>
                                                    <Radio.Button value={true} style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Dèbil</Radio.Button>
                                                </Radio.Group>
                                            </div>
                                        </div>
                                    )}

                                    {/* 2. EDITAR RELACIÓ */}
                                    {currentRel && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Nom de la Relació:</Text>
                                                <Input
                                                    size="small"
                                                    value={currentRel.name}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            relationships: prev.relationships.map(r => r.id === currentRel.id ? { ...r, name: val } : r)
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Tipus de Relació:</Text>
                                                <Radio.Group
                                                    size="small"
                                                    value={currentRel.isWeak}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            relationships: prev.relationships.map(r => r.id === currentRel.id ? { ...r, isWeak: val } : r)
                                                        }));
                                                    }}
                                                    style={{ display: 'flex', width: '100%' }}
                                                >
                                                    <Radio.Button value={false} style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Regular</Radio.Button>
                                                    <Radio.Button value={true} style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Dèbil</Radio.Button>
                                                </Radio.Group>
                                            </div>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Cardinalitat al Rombe:</Text>
                                                <Select
                                                    size="small"
                                                    value={currentRel.cardinality || '1:N'}
                                                    onChange={(val: ErCardinality) => {
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            relationships: prev.relationships.map(r => r.id === currentRel.id ? { ...r, cardinality: val } : r)
                                                        }));
                                                    }}
                                                    style={{ width: '100%' }}
                                                >
                                                    <Option value="1:1">1:1</Option>
                                                    <Option value="1:N">1:N</Option>
                                                    <Option value="N:1">N:1</Option>
                                                    <Option value="N:N">N:N</Option>
                                                </Select>
                                            </div>
                                        </div>
                                    )}

                                    {/* 3. EDITAR ATRIBUT */}
                                    {currentAttr && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Nom de l'Atribut:</Text>
                                                <Input
                                                    size="small"
                                                    value={currentAttr.name}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            attributes: prev.attributes.map(a => a.id === currentAttr.id ? { ...a, name: val } : a)
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Subtipus d'Atribut:</Text>
                                                <Radio.Group
                                                    size="small"
                                                    value={currentAttr.isPK ? 'pk' : currentAttr.isWeakPK ? 'weak_pk' : currentAttr.isMultivalued ? 'multi' : currentAttr.isDerived ? 'derived' : 'regular'}
                                                    onChange={e => {
                                                        const mode = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            attributes: prev.attributes.map(a => a.id === currentAttr.id ? {
                                                                ...a,
                                                                isPK: mode === 'pk',
                                                                isWeakPK: mode === 'weak_pk',
                                                                isMultivalued: mode === 'multi',
                                                                isDerived: mode === 'derived'
                                                            } : a)
                                                        }));
                                                    }}
                                                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                                                >
                                                    <Radio value="regular">⚪ Regular (Normal)</Radio>
                                                    <Radio value="pk">🔑 Clau Primària (PK)</Radio>
                                                    <Radio value="weak_pk">🏷️ Clau Dèbil / Parcial</Radio>
                                                    <Radio value="multi">⭕ Multivaluat (Doble)</Radio>
                                                    <Radio value="derived">✂️ Derivat (Discontinu)</Radio>
                                                </Radio.Group>
                                            </div>
                                        </div>
                                    )}

                                    {/* 4. EDITAR CONNEXIÓ */}
                                    {currentConn && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Participació / Cardinalitat:</Text>
                                                <Select
                                                    size="small"
                                                    value={currentConn.participation || '(1,1)'}
                                                    onChange={(val: ErParticipation) => {
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            connections: prev.connections.map(c => c.id === currentConn.id ? { ...c, participation: val } : c)
                                                        }));
                                                    }}
                                                    style={{ width: '100%' }}
                                                >
                                                    <Option value="(0,1)">(0,1) - Opcional simple</Option>
                                                    <Option value="(1,1)">(1,1) - Obligatòria simple</Option>
                                                    <Option value="(0,N)">(0,N) - Opcional múltiple</Option>
                                                    <Option value="(1,N)">(1,N) - Obligatòria múltiple</Option>
                                                </Select>
                                            </div>
                                        </div>
                                    )}

                                    {/* 5. EDITAR JERARQUIA */}
                                    {currentHier && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Cobertura:</Text>
                                                <Radio.Group
                                                    size="small"
                                                    value={currentHier.coverage}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            hierarchies: prev.hierarchies.map(h => h.id === currentHier.id ? { ...h, coverage: val } : h)
                                                        }));
                                                    }}
                                                    style={{ display: 'flex', width: '100%' }}
                                                >
                                                    <Radio.Button value="t" style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Total (t)</Radio.Button>
                                                    <Radio.Button value="p" style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Parcial (p)</Radio.Button>
                                                </Radio.Group>
                                            </div>
                                            <div>
                                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Disjunció:</Text>
                                                <Radio.Group
                                                    size="small"
                                                    value={currentHier.disjointness}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setDiagram(prev => ({
                                                            ...prev,
                                                            hierarchies: prev.hierarchies.map(h => h.id === currentHier.id ? { ...h, disjointness: val } : h)
                                                        }));
                                                    }}
                                                    style={{ display: 'flex', width: '100%' }}
                                                >
                                                    <Radio.Button value="d" style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Disjunta (d)</Radio.Button>
                                                    <Radio.Button value="s" style={{ flex: 1, textAlign: 'center', fontSize: 11 }}>Solapada (s)</Radio.Button>
                                                </Radio.Group>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    color: '#8c8c8c',
                                    padding: '24px 8px',
                                    fontSize: 12,
                                    border: '1px dashed #d9d9d9',
                                    borderRadius: 8
                                }}>
                                    Clica sobre qualsevol entitat, relació, atribut o jerarquia del llenç per editar els seus detalls.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Àrea del Llenç Gràfic SVG */}
                <div style={{ flex: 1, position: 'relative', overflow: 'auto', background: '#fafbfc' }}>
                    <svg
                        ref={svgRef}
                        width={2400}
                        height={1800}
                    style={{
                        transform: `scale(${zoom})`,
                        transformOrigin: '0 0',
                        cursor: toolMode === 'select' ? 'default' : 'crosshair'
                    }}
                    onClick={handleSvgClick}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                >
                    {/* Graella de fons */}
                    <defs>
                        <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#f0f0f0" strokeWidth="1" />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid)" />

                    {/* 1. Línies de Connexió */}
                    {diagram.connections.map(conn => {
                        const fromC = getNodeCenter(conn.from);
                        const toC = getNodeCenter(conn.to);
                        const isSelected = selectedElement?.id === conn.id;

                        // Detecció 100% automàtica: si dos o més enllaços connecten exactament la mateixa parella (entitat <-> relació)
                        const siblingConnections = diagram.connections.filter(c => 
                            (c.from === conn.from && c.to === conn.to) || (c.from === conn.to && c.to === conn.from)
                        );
                        const isSibling = siblingConnections.length > 1;
                        const siblingIndex = siblingConnections.findIndex(c => c.id === conn.id);

                        let pathD = '';
                        let labelX = (fromC.x + toC.x) / 2;
                        let labelY = (fromC.y + toC.y) / 2;

                        if (isSibling) {
                            // Calcular vector perpendicular per a la corba de Bézier quadràtica
                            const dx = toC.x - fromC.x;
                            const dy = toC.y - fromC.y;
                            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                            const normX = -dy / dist;
                            const normY = dx / dist;

                            // Curvatura oposada automàtica: un arc a +65px i l'altre a -65px (o successius si n'hi hagués més)
                            const offset = siblingIndex % 2 === 0 ? 65 : -65;

                            const ctrlX = (fromC.x + toC.x) / 2 + normX * offset;
                            const ctrlY = (fromC.y + toC.y) / 2 + normY * offset;

                            pathD = `M ${fromC.x} ${fromC.y} Q ${ctrlX} ${ctrlY} ${toC.x} ${toC.y}`;
                            // Punt mitjà a la corba quadràtica (t = 0.5): B(0.5) = 0.25*P0 + 0.5*Pctrl + 0.25*P1
                            labelX = 0.25 * fromC.x + 0.5 * ctrlX + 0.25 * toC.x;
                            labelY = 0.25 * fromC.y + 0.5 * ctrlY + 0.25 * toC.y;
                        }

                        return (
                            <g
                                key={conn.id}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedElement({ type: 'connection', id: conn.id });
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                {isSibling ? (
                                    <path
                                        d={pathD}
                                        fill="none"
                                        stroke={isSelected ? '#1890ff' : '#4a5568'}
                                        strokeWidth={isSelected ? 3 : 2}
                                    />
                                ) : (
                                    <line
                                        x1={fromC.x}
                                        y1={fromC.y}
                                        x2={toC.x}
                                        y2={toC.y}
                                        stroke={isSelected ? '#1890ff' : '#4a5568'}
                                        strokeWidth={isSelected ? 3 : 2}
                                    />
                                )}
                                {conn.participation && (
                                    <g transform={`translate(${labelX}, ${labelY})`}>
                                        <rect
                                            x={-24}
                                            y={-12}
                                            width={48}
                                            height={20}
                                            rx={4}
                                            fill="#ffffff"
                                            stroke={isSelected ? '#1890ff' : '#cbd5e1'}
                                            strokeWidth={1}
                                        />
                                        <text
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            y={-1}
                                            fontSize={11}
                                            fontWeight="bold"
                                            fill="#1e293b"
                                        >
                                            {conn.participation}
                                        </text>
                                    </g>
                                )}
                            </g>
                        );
                    })}

                    {/* 2. Jerarquies (Triangles d'Herència) */}
                    {diagram.hierarchies.map(h => {
                        const isSelected = selectedElement?.id === h.id;
                        const points = `${h.x},${h.y - 25} ${h.x - 30},${h.y + 25} ${h.x + 30},${h.y + 25}`;
                        return (
                            <g
                                key={h.id}
                                transform={`translate(0, 0)`}
                                onMouseDown={(e) => handleMouseDown(h.id, e)}
                                onClick={(e) => handleNodeClickForConnection(h.id, 'hierarchy', e)}
                                style={{ cursor: 'move' }}
                            >
                                <polygon
                                    points={points}
                                    fill={isSelected ? '#e6f7ff' : '#ffffff'}
                                    stroke={isSelected ? '#1890ff' : '#2b6cb0'}
                                    strokeWidth={isSelected ? 3 : 2}
                                />
                                <text
                                    x={h.x}
                                    y={h.y + 10}
                                    textAnchor="middle"
                                    fontSize={12}
                                    fontWeight="bold"
                                    fill="#2b6cb0"
                                >
                                    ({h.coverage},{h.disjointness})
                                </text>
                            </g>
                        );
                    })}

                    {/* 3. Entitats (Rectangles) */}
                    {diagram.entities.map(ent => {
                        const isSelected = selectedElement?.id === ent.id;
                        const w = ent.width || ENTITY_W;
                        const h = ent.height || ENTITY_H;

                        return (
                            <g
                                key={ent.id}
                                transform={`translate(${ent.x}, ${ent.y})`}
                                onMouseDown={(e) => handleMouseDown(ent.id, e)}
                                onClick={(e) => handleNodeClickForConnection(ent.id, 'entity', e)}
                                style={{ cursor: 'move' }}
                            >
                                {/* Rectangle extern */}
                                <rect
                                    width={w}
                                    height={h}
                                    rx={4}
                                    fill={isSelected ? '#e6f7ff' : '#ffffff'}
                                    stroke={isSelected ? '#1890ff' : '#1f2937'}
                                    strokeWidth={isSelected ? 2.5 : 2}
                                    strokeDasharray={ent.isWeak ? '6,3' : undefined}
                                />

                                {/* Rectangle interior per a entitats febles */}
                                {ent.isWeak && (
                                    <rect
                                        x={4}
                                        y={4}
                                        width={w - 8}
                                        height={h - 8}
                                        rx={2}
                                        fill="none"
                                        stroke={isSelected ? '#1890ff' : '#1f2937'}
                                        strokeWidth={1.5}
                                        strokeDasharray="5,3"
                                    />
                                )}

                                <text
                                    x={w / 2}
                                    y={h / 2}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize={13}
                                    fontWeight="bold"
                                    fill="#111827"
                                    style={{ userSelect: 'none' }}
                                >
                                    {ent.name}
                                </text>
                            </g>
                        );
                    })}

                    {/* 4. Relacions (Rombes) */}
                    {diagram.relationships.map(rel => {
                        const isSelected = selectedElement?.id === rel.id;
                        const size = rel.width || REL_SIZE;
                        const half = size / 2;
                        const points = `${half},0 ${size},${half} ${half},${size} 0,${half}`;
                        const innerPoints = `${half},4 ${size - 4},${half} ${half},${size - 4} 4,${half}`;

                        return (
                            <g
                                key={rel.id}
                                transform={`translate(${rel.x}, ${rel.y})`}
                                onMouseDown={(e) => handleMouseDown(rel.id, e)}
                                onClick={(e) => handleNodeClickForConnection(rel.id, 'relationship', e)}
                                style={{ cursor: 'move' }}
                            >
                                <polygon
                                    points={points}
                                    fill={isSelected ? '#e6f7ff' : '#ffffff'}
                                    stroke={isSelected ? '#1890ff' : '#047857'}
                                    strokeWidth={isSelected ? 2.5 : 2}
                                    strokeDasharray={rel.isWeak ? '5,3' : undefined}
                                />

                                {rel.isWeak && (
                                    <polygon
                                        points={innerPoints}
                                        fill="none"
                                        stroke={isSelected ? '#1890ff' : '#047857'}
                                        strokeWidth={1.5}
                                        strokeDasharray="4,2"
                                    />
                                )}

                                <text
                                    x={half}
                                    y={half}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize={11}
                                    fontWeight="bold"
                                    fill="#065f46"
                                    style={{ userSelect: 'none' }}
                                >
                                    {rel.name}
                                </text>

                                {rel.cardinality && (
                                    <text
                                        x={half}
                                        y={size + 14}
                                        textAnchor="middle"
                                        fontSize={10}
                                        fontWeight="bold"
                                        fill="#047857"
                                    >
                                        [{rel.cardinality}]
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* 5. Atributs (Òvals) */}
                    {diagram.attributes.map(attr => {
                        const isSelected = selectedElement?.id === attr.id;
                        const rx = ATTR_RX;
                        const ry = ATTR_RY;

                        return (
                            <g
                                key={attr.id}
                                transform={`translate(${attr.x}, ${attr.y})`}
                                onMouseDown={(e) => handleMouseDown(attr.id, e)}
                                onClick={(e) => handleNodeClickForConnection(attr.id, 'attribute', e)}
                                style={{ cursor: 'move' }}
                            >
                                <ellipse
                                    cx={0}
                                    cy={0}
                                    rx={rx}
                                    ry={ry}
                                    fill={isSelected ? '#e6f7ff' : '#ffffff'}
                                    stroke={isSelected ? '#1890ff' : (attr.isPK ? '#b91c1c' : (attr.isWeakPK ? '#d97706' : '#4b5563'))}
                                    strokeWidth={isSelected ? 2.5 : (attr.isPK || attr.isWeakPK ? 2 : 1.5)}
                                    strokeDasharray={attr.isDerived ? '4,3' : undefined}
                                />

                                {attr.isMultivalued && (
                                    <ellipse
                                        cx={0}
                                        cy={0}
                                        rx={rx - 4}
                                        ry={ry - 4}
                                        fill="none"
                                        stroke={isSelected ? '#1890ff' : '#4b5563'}
                                        strokeWidth={1.5}
                                    />
                                )}

                                <text
                                    x={0}
                                    y={attr.isPK || attr.isWeakPK ? -2 : 0}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize={11}
                                    fontWeight={attr.isPK || attr.isWeakPK ? 'bold' : 'normal'}
                                    fill={attr.isPK ? '#b91c1c' : (attr.isWeakPK ? '#b45309' : '#1f2937')}
                                    style={{
                                        textDecoration: attr.isPK ? 'underline' : 'none',
                                        userSelect: 'none'
                                    }}
                                >
                                    {attr.name}
                                </text>

                                {/* Per a clau dèbil / discriminador parcial: l'òval és normal (sòlid) i el subratllat és discontinu */}
                                {attr.isWeakPK && (
                                    <line
                                        x1={-Math.min(rx - 8, Math.max(12, attr.name.length * 3.6))}
                                        y1={6}
                                        x2={Math.min(rx - 8, Math.max(12, attr.name.length * 3.6))}
                                        y2={6}
                                        stroke="#b45309"
                                        strokeWidth={1.5}
                                        strokeDasharray="3,2"
                                    />
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>

            {/* Modal de Feedback de la Validació Automàtica */}
            <Modal
                title={
                    <Space>
                        {evaluationResult?.isCorrect ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 22 }} />
                        ) : (
                            <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 22 }} />
                        )}
                        <span style={{ fontWeight: 700 }}>
                            {evaluationResult?.isCorrect ? 'Exercici ER Superat amb Èxit!' : 'Correcció del Model ER'}
                        </span>
                    </Space>
                }
                open={evalModalVisible}
                onCancel={() => setEvalModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setEvalModalVisible(false)}>
                        Tancar
                    </Button>
                ]}
                width={650}
            >
                {evaluationResult && (
                    <div>
                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            <Title level={2} style={{ margin: 0, color: evaluationResult.isCorrect ? '#52c41a' : '#f5222d' }}>
                                {evaluationResult.score} / 100
                            </Title>
                            <Text type="secondary">
                                {evaluationResult.penalitzacioTotal && evaluationResult.penalitzacioTotal > 0 ? (
                                    <span>
                                        Nota base: <strong>{evaluationResult.notaBase || 0} pts</strong> · Penalització: <strong style={{ color: '#f5222d' }}>-{evaluationResult.penalitzacioTotal} pts</strong> ({evaluationResult.intentsFallitsPrevis} intents)
                                    </span>
                                ) : (
                                    "Puntuació obtinguda en base a la rúbrica i tests de disseny"
                                )}
                            </Text>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Card size="small" style={{ background: '#fafafa' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>Entitats</div>
                                        <div style={{ color: '#1890ff' }}>{evaluationResult.summary.entitiesMatched} / {evaluationResult.summary.entitiesExpected}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>Relacions</div>
                                        <div style={{ color: '#1890ff' }}>{evaluationResult.summary.relationshipsMatched} / {evaluationResult.summary.relationshipsExpected}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>Atributs / PKs</div>
                                        <div style={{ color: '#1890ff' }}>{evaluationResult.summary.attributesMatched} / {evaluationResult.summary.attributesExpected}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>Connexions</div>
                                        <div style={{ color: '#1890ff' }}>{evaluationResult.summary.connectionsMatched} / {evaluationResult.summary.connectionsExpected}</div>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <Title level={5}>Detall de Correcció:</Title>
                        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                            {evaluationResult.feedback.map((item, idx) => (
                                <Alert
                                    key={idx}
                                    type={item.type}
                                    showIcon
                                    message={item.message}
                                    style={{ marginBottom: 8 }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ErDiagramIde;
